import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Loader2, Sparkles, ArrowRight, TrendingUp, Clock } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useLang } from "../context/LangContext";

const MobileSearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState('normal');
  const [trendTags, setTrendTags] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useLang();

  // Load recent searches and trends
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      const saved = localStorage.getItem('loft_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
      
      // Fetch trend tags
      axios.get(`${API}/ai/trends?size=5`)
        .then(r => setTrendTags((r.data.stories || []).map(s => s.name).filter(Boolean).slice(0, 5)))
        .catch(() => {});
    }
  }, [isOpen]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSearch = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      if (searchMode === 'ai') {
        const response = await axios.get(`${API}/ai/search?q=${encodeURIComponent(q)}&size=5`);
        setResults(response.data.map(a => ({ 
          id: null, title: a.title, summary: a.description, 
          image_url: a.image_url, category: a.source, url: a.url, is_external: true 
        })));
      } else {
        const response = await axios.get(`${API}/news/search?q=${encodeURIComponent(q)}&limit=5`);
        setResults(response.data);
      }
    } catch (e) { console.error(e); } 
    finally { setSearching(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    // Save to recent searches
    const updated = [query.trim(), ...recentSearches.filter(s => s !== query.trim())].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('loft_recent_searches', JSON.stringify(updated));
    
    onClose();
    setQuery('');
    setResults([]);
    navigate(`/?search=${encodeURIComponent(query.trim())}`);
  };

  const handleResultClick = (item) => {
    // Save to recent
    if (query.trim()) {
      const updated = [query.trim(), ...recentSearches.filter(s => s !== query.trim())].slice(0, 5);
      localStorage.setItem('loft_recent_searches', JSON.stringify(updated));
    }
    onClose();
    setQuery('');
    setResults([]);
  };

  const handleTrendClick = (tag) => {
    setQuery(tag);
    handleSearch(tag);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('loft_recent_searches');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#0a0a0b]" data-testid="mobile-search-modal">
      {/* Header */}
      <div className="sticky top-0 bg-[#0a0a0b] border-b border-zinc-800/50 px-4 py-3 safe-area-top">
        <form onSubmit={handleSubmit} className="flex items-center gap-3">
          <button 
            type="button"
            onClick={onClose}
            className="p-2 text-zinc-400"
            data-testid="mobile-search-close"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={searchMode === 'ai' ? t('searchAI') : t('search')}
              className={`w-full pl-10 pr-4 py-3 bg-zinc-900 border rounded-xl text-white placeholder:text-zinc-500 focus:outline-none transition-colors ${
                searchMode === 'ai' ? 'border-blue-500/50 focus:border-blue-500' : 'border-zinc-700 focus:border-amber-500'
              }`}
              data-testid="mobile-search-input"
            />
          </div>
        </form>

        {/* Search Mode Toggle */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={() => { setSearchMode('normal'); setResults([]); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
              searchMode === 'normal' ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-400'
            }`}
            data-testid="mobile-search-normal"
          >
            <Search className="w-4 h-4" /> Normal Arama
          </button>
          <button
            onClick={() => { setSearchMode('ai'); setResults([]); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
              searchMode === 'ai' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-400'
            }`}
            data-testid="mobile-search-ai"
          >
            <Sparkles className="w-4 h-4" /> AI Arama
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        {query.length >= 2 ? (
          /* Search Results */
          <div className="px-4 py-4">
            {searching ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-3" />
                <p className="text-sm text-zinc-500">
                  {searchMode === 'ai' ? t('aiAnalyzing') : t('searching')}
                </p>
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-3">
                {results.map((item, idx) => (
                  item.is_external ? (
                    <a
                      key={idx}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleResultClick(item)}
                      className="flex items-center gap-3 p-3 bg-zinc-900/50 hover:bg-zinc-800/50 rounded-xl transition-colors"
                    >
                      <img 
                        src={item.image_url} 
                        alt="" 
                        className="w-16 h-12 rounded-lg object-cover flex-shrink-0"
                        onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200'}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white line-clamp-2">{item.title}</h4>
                        <p className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> AI Sonuç
                        </p>
                      </div>
                    </a>
                  ) : (
                    <Link
                      key={item.id}
                      to={`/haber/${item.slug || item.id}`}
                      onClick={() => handleResultClick(item)}
                      className="flex items-center gap-3 p-3 bg-zinc-900/50 hover:bg-zinc-800/50 rounded-xl transition-colors"
                    >
                      <img 
                        src={item.image_url} 
                        alt="" 
                        className="w-16 h-12 rounded-lg object-cover flex-shrink-0"
                        onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200'}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-white line-clamp-2">{item.title}</h4>
                        <p className="text-xs text-zinc-500 mt-1">{item.category}</p>
                      </div>
                    </Link>
                  )
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">{t('noResults')}</p>
              </div>
            )}
          </div>
        ) : (
          /* Initial State: Trends + Recent */
          <div className="px-4 py-4 space-y-6">
            {/* Trend Tags */}
            {trendTags.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                  <p className="text-sm font-semibold text-white">Trend Konular</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {trendTags.map((tag, i) => (
                    <button
                      key={i}
                      onClick={() => handleTrendClick(tag)}
                      className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm rounded-full transition-colors"
                    >
                      {tag.length > 30 ? tag.slice(0, 30) + '...' : tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-zinc-500" />
                    <p className="text-sm font-semibold text-white">Son Aramalar</p>
                  </div>
                  <button 
                    onClick={clearRecentSearches}
                    className="text-xs text-zinc-500 hover:text-zinc-300"
                  >
                    Temizle
                  </button>
                </div>
                <div className="space-y-1">
                  {recentSearches.map((term, i) => (
                    <button
                      key={i}
                      onClick={() => { setQuery(term); handleSearch(term); }}
                      className="w-full flex items-center justify-between p-3 bg-zinc-900/50 hover:bg-zinc-800/50 rounded-xl transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-zinc-600" />
                        <span className="text-sm text-zinc-300">{term}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-zinc-600" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search Tips */}
            <div className="mt-8 p-4 bg-zinc-900/30 rounded-xl border border-zinc-800/50">
              <p className="text-xs text-zinc-500 mb-2">Arama İpuçları</p>
              <ul className="text-xs text-zinc-400 space-y-1">
                <li>• Haber başlıklarında arama yapmak için normal aramayı kullanın</li>
                <li>• Daha derin ve anlam bazlı aramalar için AI Arama'yı deneyin</li>
                <li>• En az 2 karakter girin</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSearchModal;
